import { type AppearanceTypes, AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import StatusWarningIcon from '@atlaskit/icon/core/status-warning';
import { token } from '@atlaskit/tokens';
import { type FlagsManager, type Flag } from '@databases/flags-types';
import React, { type ReactNode } from 'react';
import { useSnapshot } from 'valtio';

const unreachable = (value: never) => {
  throw new Error(`Not implemented: ${value}`);
};

interface DefaultFlagGroupProps {
  flags: { data: Flag[] };
  handleFlagDismiss: FlagsManager['handleFlagDismiss'];
}

/**
 * A default flag group that renders flags using the `@atlaskit/flag` component.
 * This is used locally in the databases app, though it will not be implemented when we
 * defer flags to Confluence.
 */
export function DefaultFlagGroup(props: DefaultFlagGroupProps) {
  const snapshot = useSnapshot(props.flags);

  const getIcon = (appearance: AppearanceTypes): ReactNode => {
    switch (appearance) {
      case 'info':
        return <StatusInformationIcon spacing="spacious" label="" />;
      case 'warning':
        return (
          <StatusWarningIcon
            spacing="spacious"
            label=""
            color={token('color.icon.warning', '#E2B203')}
          />
        );
      case 'error':
        return (
          <StatusErrorIcon
            spacing="spacious"
            label=""
            color={token('color.icon.danger', '#C9372C')}
          />
        );
      case 'success':
        return <StatusSuccessIcon spacing="spacious" label="" />;
      case 'normal':
        return (
          <StatusInformationIcon
            spacing="spacious"
            label=""
            color={token('color.icon.information', '#1D7AFC')}
          />
        );
      default:
        unreachable(appearance);
    }
  };

  return (
    <FlagGroup
      onDismissed={(flagId) => {
        props.handleFlagDismiss(flagId);
      }}
    >
      {snapshot.data.map((flag) => {
        const flagAppearance = flag.appearance ?? 'normal';

        return (
          <AutoDismissFlag
            appearance={flagAppearance}
            id={flag.id}
            testId="database-flag"
            icon={flag.icon ? flag.icon() : getIcon(flagAppearance)}
            key={flag.id}
            title={flag.title}
            description={flag.description}
            actions={flag.actions ? [...flag.actions] : undefined}
          />
        );
      })}
    </FlagGroup>
  );
}
